// Regras de roteamento e de escalonamento.
//
// Roteamento decide PARA ONDE o chamado vai na abertura. As regras são
// avaliadas de cima para baixo e vale a primeira que casa; dentro de uma
// regra, todas as condições precisam ser verdadeiras. Nenhuma casou, vale a
// área do assunto, e sem ela a área padrão do sistema: a cadeia sempre termina
// com uma área válida (§4.2).
//
// Escalonamento decide O QUE ACONTECE quando o chamado demora. Aqui ficam só
// os contratos; a avaliação mora nos motores do backend.
import { z } from 'zod';
import { PRIORITIES, TICKET_CHANNELS } from './enums.js';
/* ── Roteamento ──────────────────────────────────────────────────────────── */
export const ROUTING_FIELDS = [
    'sistema',
    'categoria',
    'assunto',
    'empresa',
    'projeto',
    'prioridade',
    'canal',
    'horario',
];
export const ROUTING_OPERATORS = ['e', 'nao_e', 'contem'];
export const ROUTING_FIELD_LABEL = {
    sistema: 'Sistema',
    categoria: 'Categoria',
    assunto: 'Assunto',
    empresa: 'Empresa',
    projeto: 'Projeto',
    prioridade: 'Prioridade',
    canal: 'Canal de abertura',
    horario: 'Horário',
};
export const ROUTING_OPERATOR_LABEL = {
    e: 'é',
    nao_e: 'não é',
    contem: 'contém',
};
/** Valores de `horario`, o único campo que não aponta para um cadastro. */
export const ROUTING_SCHEDULE_VALUES = ['dentro', 'fora'];
export const ROUTING_SCHEDULE_LABEL = {
    dentro: 'dentro do atendimento',
    fora: 'fora do atendimento',
};
/** Campos cujo valor é o id de um cadastro, e não texto livre. */
export const ROUTING_ID_FIELDS = [
    'sistema',
    'categoria',
    'assunto',
    'empresa',
    'projeto',
];
export const routingConditionSchema = z.object({
    field: z.enum(ROUTING_FIELDS),
    operator: z.enum(ROUTING_OPERATORS).default('e'),
    value: z.string().trim().min(1, 'Informe o valor da condição'),
});
export const routingRuleInputSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    position: z.number().int().min(1).max(999),
    conditions: z.array(routingConditionSchema).min(1, 'A regra precisa de uma condição'),
    targetAreaId: z.string().uuid().nullable().default(null),
    targetAssigneeId: z.string().uuid().nullable().default(null),
    setPriority: z.enum(PRIORITIES).nullable().default(null),
    addTagId: z.string().uuid().nullable().default(null),
    active: z.boolean().default(true),
})
    .refine((v) => v.targetAreaId !== null ||
    v.targetAssigneeId !== null ||
    v.setPriority !== null ||
    v.addTagId !== null, {
    message: 'A regra precisa fazer alguma coisa: escolha pelo menos uma ação',
    path: ['targetAreaId'],
})
    .refine((v) => v.targetAssigneeId === null || v.targetAreaId !== null, {
    message: 'Para atribuir a alguém, escolha também a área de destino',
    path: ['targetAssigneeId'],
});
export const ROUTING_RULE_SORTABLE = ['position', 'name', 'active', 'createdAt'];
/* ── Simulador ───────────────────────────────────────────────────────────── */
export const routingSimulationSchema = z.object({
    systemId: z.string().uuid().nullable().default(null),
    categoryId: z.string().uuid().nullable().default(null),
    subjectId: z.string().uuid().nullable().default(null),
    companyId: z.string().uuid().nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
    priority: z.enum(PRIORITIES).default('media'),
    channel: z.enum(TICKET_CHANNELS).default('portal'),
    withinSchedule: z.boolean().default(true),
});
/* ── Escalonamento ───────────────────────────────────────────────────────── */
export const ESCALATION_TRIGGERS = [
    'first_response_pct',
    'resolution_pct',
    'resolution_due',
    'unassigned_minutes',
    'idle_hours',
    'client_reply_minutes',
];
export const ESCALATION_TRIGGER_LABEL = {
    first_response_pct: 'A primeira resposta consumir uma parte do prazo',
    resolution_pct: 'A solução consumir uma parte do prazo',
    resolution_due: 'O prazo de solução vencer',
    unassigned_minutes: 'O chamado ficar um tempo na fila sem responsável',
    idle_hours: 'O chamado ficar um tempo sem atualização',
    client_reply_minutes: 'O cliente responder e ninguém retornar',
};
/** Unidade do valor do gatilho, para a tela rotular o campo. */
export const ESCALATION_TRIGGER_UNIT = {
    first_response_pct: '% do prazo',
    resolution_pct: '% do prazo',
    resolution_due: null,
    unassigned_minutes: 'minutos',
    idle_hours: 'horas',
    client_reply_minutes: 'minutos',
};
/** Gatilhos que contam sobre o relógio de SLA. Nestes o calendário é o da
 *  política, e a opção de contar pelo calendário da área não se aplica (§5.5). */
export const SLA_TRIGGERS = [
    'first_response_pct',
    'resolution_pct',
    'resolution_due',
];
export const ESCALATION_ACTIONS = [
    'notify_only',
    'next_level',
    'assign_to_leader',
    'raise_priority',
    'mark_breached',
];
export const ESCALATION_ACTION_LABEL = {
    notify_only: 'Só avisar',
    next_level: 'Subir para o próximo nível',
    assign_to_leader: 'Atribuir ao líder da área',
    raise_priority: 'Subir a prioridade para Alta',
    mark_breached: 'Marcar o chamado como fora do prazo',
};
export const ESCALATION_ACTION_HINT = {
    notify_only: 'O chamado continua com quem já estava atendendo.',
    next_level: 'Segue a cadeia de níveis da área.',
    assign_to_leader: 'O líder passa a ser o responsável.',
    raise_priority: 'A prioridade do chamado passa a ser Alta.',
    mark_breached: 'O chamado passa a contar como fora do prazo nos indicadores.',
};
export const escalationRuleInputSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    trigger: z.enum(ESCALATION_TRIGGERS),
    triggerValue: z.number().int().min(0).max(100_000).default(0),
    countInAreaCalendar: z.boolean().default(false),
    /** Vazio significa todas. */
    scopeAreaIds: z.array(z.string().uuid()).default([]),
    scopePriorities: z.array(z.enum(PRIORITIES)).default([]),
    notifyAssignee: z.boolean().default(true),
    notifyAreaLeader: z.boolean().default(false),
    notifySupportManager: z.boolean().default(false),
    action: z.enum(ESCALATION_ACTIONS).default('notify_only'),
    channelEmail: z.boolean().default(true),
    channelPanel: z.boolean().default(true),
    repeatMinutes: z.number().int().min(0).max(10_000).default(0),
    maxRepeats: z.number().int().min(0).max(100).default(0),
    active: z.boolean().default(true),
})
    .refine((v) => v.trigger === 'resolution_due' || v.triggerValue > 0, {
    message: 'Informe o valor do gatilho',
    path: ['triggerValue'],
})
    .refine((v) => !v.trigger.endsWith('_pct') || (v.triggerValue >= 1 && v.triggerValue <= 100), { message: 'A parte do prazo vai de 1 a 100', path: ['triggerValue'] })
    .refine((v) => v.notifyAssignee ||
    v.notifyAreaLeader ||
    v.notifySupportManager ||
    v.action !== 'notify_only', {
    message: 'Escolha quem avisar, ou uma ação além de avisar',
    path: ['notifyAssignee'],
})
    .refine((v) => v.channelEmail || v.channelPanel, {
    message: 'Escolha pelo menos um canal de aviso',
    path: ['channelEmail'],
});
export const ESCALATION_RULE_SORTABLE = [
    'name',
    'trigger',
    'action',
    'active',
    'createdAt',
];
/** "A solução consumir 80% do prazo", "O chamado ficar 30 minutos na fila". */
export function describeTrigger(trigger, value) {
    switch (trigger) {
        case 'first_response_pct':
            return `A primeira resposta consumir ${value}% do prazo`;
        case 'resolution_pct':
            return `A solução consumir ${value}% do prazo`;
        case 'resolution_due':
            return 'O prazo de solução vencer';
        case 'unassigned_minutes':
            return `O chamado ficar ${value} ${value === 1 ? 'minuto' : 'minutos'} na fila sem responsável`;
        case 'idle_hours':
            return `O chamado ficar ${value} ${value === 1 ? 'hora' : 'horas'} sem atualização`;
        case 'client_reply_minutes':
            return `O cliente responder e ninguém retornar em ${value} ${value === 1 ? 'minuto' : 'minutos'}`;
    }
}
